<template>
  <el-image
    v-if="useElementUI && src"
    :src="src"
    :alt="getAlt"
    :fit="fit"
    :lazy="lazy"
    :preview-src-list="previewSrcList"
    :z-index="zIndex"
    :preview-teleported="previewTeleported"
    :initial-index="initialIndex"
    :hide-on-click-modal="hideOnClickModal"
    :loading="loading"
    :error="error"
    :class="imageClass"
    :style="imageStyle"
    @load="handleLoad"
    @error="handleError"
    @switch="handleSwitch"
    @close="handleClose"
    @show="handleShow"
    @click="handleClick"
  >
    <template #placeholder>
      <slot name="placeholder">
        <div class="image-placeholder" :style="placeholderStyle">
          <i class="iconfont icon-icon_loading" v-if="loading === 'lazy'"></i>
        </div>
      </slot>
    </template>
    <template #error>
      <slot name="error">
        <div class="image-error" :style="errorStyle">
          <i class="iconfont icon-icon_error" v-if="!error"></i>
          <el-image :src="error" :alt="alt" :useElementUI="false" />
        </div>
      </slot>
    </template>
  </el-image>
  <img
    v-else-if="src"
    :src="src"
    :alt="getAlt"
    :class="imageClass"
    :style="imageStyle"
    :loading="nativeLoading"
    @load="handleLoad"
    @error="handleError"
    @click="handleClick"
  />
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'
// import useSeoStore from '~/store/seo'
import { storeToRefs } from 'pinia'
const route = useRoute()
// const seoStore = useSeoStore()
// const { altConfig } = storeToRefs(seoStore)
interface Props {
  // 图片源
  src?: string
  // 图片描述
  alt?: string
  // 是否使用 Element UI 的 el-image 组件
  useElementUI?: boolean
  // 图片适应方式 (仅 el-image)
  fit?: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down'
  // 是否懒加载 (仅 el-image)
  lazy?: boolean
  // 预览图片列表 (仅 el-image)
  previewSrcList?: string[]
  // 预览层级 (仅 el-image)
  zIndex?: number
  // 预览是否传送到 body (仅 el-image)
  previewTeleported?: boolean
  // 初始预览索引 (仅 el-image)
  initialIndex?: number
  // 点击遮罩层是否关闭预览 (仅 el-image)
  hideOnClickModal?: boolean
  // 加载状态 (仅 el-image)
  loading?: 'lazy' | 'eager'
  // 错误图片 (仅 el-image)
  error?: string
  // 自定义类名
  imageClass?: string | string[] | Record<string, boolean>
  // 自定义样式
  imageStyle?: string | Record<string, any>
  // 占位符样式
  placeholderStyle?: string | Record<string, any>
  // 错误样式
  errorStyle?: string | Record<string, any>
  // 原生图片加载方式
  nativeLoading?: 'lazy' | 'eager'
}

interface Emits {
  load: [event: Event]
  error: [event: Event]
  switch: [index: number]
  close: []
  show: []
  click: []
}

const getAlt = computed(() => {
  // let key = props.src || ''
  // const pathWithoutLang = route.path.replace(/^\/[a-z]{2}\//, '/')
  // const currentSeoAlt = altConfig.value[pathWithoutLang]
  // console.log('currentSeoAlt=====>>111', currentSeoAlt, key)
  // if (currentSeoAlt && currentSeoAlt[key]) {
  //   console.log('currentSeoAlt=====>>', currentSeoAlt[key])
  //   return currentSeoAlt[key]
  // }
  return props.alt || ''
})

const props = withDefaults(defineProps<Props>(), {
  alt: '',
  useElementUI: true,
  fit: 'cover',
  lazy: false,
  previewSrcList: () => [],
  zIndex: 2000,
  previewTeleported: true,
  initialIndex: 0,
  hideOnClickModal: true,
  loading: 'lazy',
  error: '',
  imageClass: '',
  imageStyle: '',
  placeholderStyle: '',
  errorStyle: '',
  nativeLoading: 'lazy'
})

const emit = defineEmits<Emits>()

const handleLoad = (event: Event) => {
  emit('load', event)
}

const handleError = (event: Event) => {
  emit('error', event)
}

const handleSwitch = (index: number) => {
  emit('switch', index)
}

const handleClose = () => {
  emit('close')
}

const handleShow = () => {
  emit('show')
}

const handleClick = (event: Event) => {
  emit('click')
}
</script>

<style scoped lang="less">
.image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f7fa;
  color: #909399;
  font-size: 14px;
  min-height: 100px;
  
  .iconfont {
    font-size: 24px;
    animation: spin 1s linear infinite;
  }
}

.image-error {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f7fa;
  color: #c0c4cc;
  font-size: 14px;
  min-height: 100px;
  
  .iconfont {
    font-size: 24px;
  }
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
